import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Menu, X } from 'lucide-react';
import { webAppGalleryUrl } from '../utils/webAppUrl';
import { LocalizedLink, useLocalizedPath } from '../i18n/LocalizedLink';
import { stripLocalePrefix, SUPPORTED_LOCALES } from '../i18n/localeRouting';
import { useLocaleSwitch } from './LanguageSwitcher';

type NavLinkItem = {
  label: string;
  to: string;
};

type NavGroup = {
  label: string;
  overviewTo?: string;
  items: NavLinkItem[];
};

type LandingMobileNavMenuProps = {
  isOpen: boolean;
  isEnabled?: boolean;
  onToggle: () => void;
  onClose: () => void;
  serviceItems: NavLinkItem[];
  useCaseItems: NavLinkItem[];
  blogItems: NavLinkItem[];
};

function isPathActive(pathname: string, to: string, localize: (path: string) => string): boolean {
  const bare = stripLocalePrefix(pathname);
  const localizedTo = localize(to);
  if (pathname === localizedTo || bare === to) return true;
  if (to !== '/' && to.length > 1 && bare.startsWith(`${to}/`)) return true;
  return false;
}

export function LandingMobileNavMenu({
  isOpen,
  isEnabled = true,
  onToggle,
  onClose,
  serviceItems,
  useCaseItems,
  blogItems,
}: LandingMobileNavMenuProps) {
  const { t } = useTranslation('common');
  const localize = useLocalizedPath();
  const { pathname } = useLocation();
  const { current: currentLocale, switchLocale } = useLocaleSwitch();
  const rootRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const renderLink = (item: NavLinkItem, variant: 'primary' | 'chip' = 'chip') => {
    const active = isPathActive(pathname, item.to, localize);

    return (
      <LocalizedLink
        key={item.to}
        to={item.to}
        className={`landing_header_mobile_nav_link landing_header_mobile_nav_link--${variant}${
          active ? ' is-active' : ''
        }`}
        aria-current={active ? 'page' : undefined}
        onClick={onClose}
      >
        {item.label}
      </LocalizedLink>
    );
  };

  const renderGroup = (group: NavGroup) => {
    const overviewActive = group.overviewTo
      ? isPathActive(pathname, group.overviewTo, localize)
      : false;

    return (
      <div key={group.label} className="landing_header_mobile_nav_group">
        {group.overviewTo ? (
          <LocalizedLink
            to={group.overviewTo}
            className={`landing_header_mobile_nav_group_label${overviewActive ? ' is-active' : ''}`}
            aria-current={overviewActive ? 'page' : undefined}
            onClick={onClose}
          >
            {group.label}
          </LocalizedLink>
        ) : (
          <p className="landing_header_mobile_nav_group_label">{group.label}</p>
        )}
        <div className="landing_header_mobile_nav_group_links">
          {group.items.map((item) => renderLink(item, 'chip'))}
        </div>
      </div>
    );
  };

  const groups: NavGroup[] = [
    { label: t('nav.service'), overviewTo: '/service/features', items: serviceItems },
    { label: t('nav.useCases'), overviewTo: '/use-cases', items: useCaseItems },
    { label: t('nav.blog'), overviewTo: '/blog/insights', items: blogItems },
  ];

  return (
    <li
      ref={rootRef}
      className={`landing_header_mobile_nav${isOpen ? ' is-open' : ''}${
        isEnabled ? '' : ' landing_header_mobile_nav--hidden'
      }`}
      aria-hidden={!isEnabled}
    >
      <button
        type="button"
        className="landing_header_mobile_nav_trigger"
        aria-label={isOpen ? t('nav.menuClose') : t('nav.menuOpen')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="landing-header-mobile-nav-panel"
        tabIndex={isEnabled ? 0 : -1}
        disabled={!isEnabled}
        onClick={onToggle}
      >
        {isOpen ? (
          <X className="landing_header_mobile_nav_icon" strokeWidth={2.25} aria-hidden />
        ) : (
          <Menu className="landing_header_mobile_nav_icon" strokeWidth={2.25} aria-hidden />
        )}
      </button>

      <div
        id="landing-header-mobile-nav-panel"
        className={`landing_header_mobile_nav_panel${isOpen ? ' is-open' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="landing_header_mobile_nav_panel_inner">
          <div className="landing_header_mobile_nav_primary">
            {renderLink({ label: t('nav.brandStory'), to: '/brand-story' }, 'primary')}
            {renderLink({ label: t('nav.contact'), to: '/contact' }, 'primary')}
            <a
              href={webAppGalleryUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="landing_header_mobile_nav_link landing_header_mobile_nav_link--primary"
              onClick={onClose}
            >
              <span>{t('nav.gallery')}</span>
              <ExternalLink
                className="landing_header_mobile_nav_external"
                strokeWidth={2.25}
                aria-hidden
              />
            </a>
          </div>

          <div className="landing_header_mobile_nav_groups">{groups.map(renderGroup)}</div>

          <div className="landing_header_mobile_nav_lang">
            <div className="landing_header_mobile_nav_lang_options" role="group" aria-label={t('nav.language')}>
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale}
                  type="button"
                  className={`landing_header_mobile_nav_lang_option${
                    locale === currentLocale ? ' is-selected' : ''
                  }`}
                  aria-pressed={locale === currentLocale}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => switchLocale(locale)}
                >
                  {t(`lang.${locale}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}
